/**
 * Data-misfit crossplot (EarthImager 3D manual 3.8.5): calculated vs measured apparent resistivity on log
 * axes with the 1:1 line, coloured by the signs of the pair. Pure.
 */

/** Sign classes, EarthImager colours: both positive black, both negative green, measured negative / calculated positive blue, measured positive / calculated negative red. */
export const CROSSPLOT_CLASSES = [
  { id: 'pp', label: 'Both positive', color: '#000000' },
  { id: 'nn', label: 'Both negative', color: '#16a34a' },
  { id: 'np', label: 'Measured negative, calculated positive', color: '#2563eb' },
  { id: 'pn', label: 'Measured positive, calculated negative', color: '#dc2626' },
];

/** Class id of a measured / calculated pair (zero counts as positive). */
export function classifyPair(measured, calculated) {
  const mNeg = measured < 0, cNeg = calculated < 0;
  if (mNeg && cNeg) return 'nn';
  if (mNeg) return 'np';
  if (cNeg) return 'pn';
  return 'pp';
}

/**
 * Plot points of a data-fit block ({ rhoa_meas, rhoa_calc, seq? }). Log axes need magnitudes, so x and y
 * are |measured| and |calculated|; pairs with a zero or non-finite value are dropped (counted in `dropped`).
 * @returns {{ points: {seq, x, y, measured, calculated, misfit, cls}[], range: [number, number] | null, rms: number | null, dropped: number }}
 *  range: shared decade-rounded axis limits; rms: RMS of the relative error in %.
 */
export function crossplotData(fit) {
  const meas = fit?.rhoa_meas || [], calc = fit?.rhoa_calc || [];
  const n = Math.min(meas.length, calc.length);
  const points = [];
  let dropped = 0, sumSq = 0;
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) {
    const m = meas[i], c = calc[i];
    if (!Number.isFinite(m) || !Number.isFinite(c) || m === 0 || c === 0) { dropped++; continue; }
    const misfit = ((c - m) / m) * 100;
    points.push({ seq: fit.seq ? fit.seq[i] : i, x: Math.abs(m), y: Math.abs(c), measured: m, calculated: c, misfit, cls: classifyPair(m, c) });
    sumSq += ((c - m) / m) ** 2;
    lo = Math.min(lo, Math.abs(m), Math.abs(c));
    hi = Math.max(hi, Math.abs(m), Math.abs(c));
  }
  if (!points.length) return { points, range: null, rms: null, dropped };
  const a = 10 ** Math.floor(Math.log10(lo)), b = 10 ** Math.ceil(Math.log10(hi));
  return { points, range: [a, b > a ? b : a * 10], rms: Math.sqrt(sumSq / points.length) * 100, dropped };
}

/** Points of one sign class. */
export const pointsOfClass = (points, id) => points.filter((p) => p.cls === id);

/**
 * Iterations whose data fit the model carries: a session model's `dataFitIterations` store ({ "<n>": { calc } }),
 * or a stored model's `dataFitIterationList`. `final` is the iteration of `dataFit` (model.iteration for stored models).
 * @returns {{ list: number[], final: number | null }}
 */
export function crossplotIterations(model) {
  const set = new Set(model?.dataFitIterations ? Object.keys(model.dataFitIterations).map(Number) : (model?.dataFitIterationList || []));
  const final = model?.dataFit?.iteration ?? model?.iteration ?? null;
  if (final != null && (model?.dataFit || set.size)) set.add(final);
  return { list: [...set].filter(Number.isFinite).sort((a, b) => a - b), final };
}

/** Select options "Iteration 5 · RMS 5.18 %" for iteration numbers `list`; RMS from the convergence history [{ n, rms }]. */
export function iterationOptions(list, convergence = []) {
  const rms = new Map((convergence || []).map((c) => [c.n, c.rms]));
  return list.map((n) => {
    const r = rms.get(n);
    return { n, rms: r ?? null, label: `Iteration ${n}${Number.isFinite(r) ? ` · RMS ${Number(r).toFixed(2)} %` : ''}` };
  });
}

/** The data fit of iteration `n` from a session model's per-iteration store; `fit` itself for its own iteration, null when not stored. */
export function fitAtIteration(fit, store, n) {
  if (!fit || n == null || n === fit.iteration) return fit || null;
  const e = store?.[String(n)];
  if (!e) return null;
  return { ...fit, rhoa_calc: e.calc, vi_calc: e.vi_calc ?? fit.vi_calc, iteration: n };
}
